import { NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { GoogleMap, MapCircle, MapMarker } from '@angular/google-maps';
import { PlaceTypeEnum } from '@buspro/shared/common';
import { CircleMarkerDto, MapMarkerDto } from '@buspro/shared/dtos';
import { PlaceAutocompleteComponent } from '@buspro/web/admin/shared';
import { StandardPlaceDto, StandardPlacesApi, UpdateStandardPlaceDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, MapService, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { v4 } from 'uuid';
import { StandardPlaceImpact, StandardPlaceImpactService } from './standard-place-impact.service';
import LatLng = google.maps.LatLng;
import LatLngLiteral = google.maps.LatLngLiteral;
import { NgxSliderModule, Options } from '@angular-slider/ngx-slider';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'admin-edit-standard-place',
  templateUrl: './edit-standard-place.component.html',
  styleUrls: ['./edit-standard-place.component.css'],
  imports: [
    NzDrawerModule,
    NzButtonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzRadioModule,
    NzSliderModule,
    NgIf,
    PlaceAutocompleteComponent,
    NzInputDirective,
    GoogleMap,
    MapCircle,
    MapMarker,
    ErrorDisplayComponent,
    NzAlertModule,
    NgxSliderModule,
  ],
  standalone: true,
})
export class EditStandardPlaceComponent implements OnInit {
  private logger = inject(NGXLogger);
  private formBuilder = inject(FormBuilder);
  private mapService = inject(MapService);
  private standardPlacesApi = inject(StandardPlacesApi);
  private impactService = inject(StandardPlaceImpactService);

  /** What saving will touch, fetched per place. Null while loading/unavailable. */
  impact = signal<StandardPlaceImpact | null>(null);
  impactDescription = computed(() => {
    const counts = this.impact();
    if (!counts) {
      // Impact unknown (still loading or the endpoint failed) — warn generically.
      return (
        'Saving renames this place across every company that uses it — routes, sub-routes, ' +
        'bus stops, and trips currently in progress. Completed trips and audit records keep the old name. ' +
        'Only edit to correct THIS place; to point somewhere else, create a new place instead.'
      );
    }
    if (counts.tenantPlaces === 0) {
      return 'No company uses this place yet — saving only changes the official record.';
    }
    return (
      `Saving updates ${counts.routes} route${counts.routes === 1 ? '' : 's'}, ` +
      `${counts.subRoutes} sub-route${counts.subRoutes === 1 ? '' : 's'} and ` +
      `${counts.busStops} bus stop${counts.busStops === 1 ? '' : 's'} across ` +
      `${counts.companies} compan${counts.companies === 1 ? 'y' : 'ies'}, immediately — including ` +
      `${counts.activeOperations} trip${counts.activeOperations === 1 ? '' : 's'} in progress and ` +
      `${counts.liveFutureBookings} future booking${counts.liveFutureBookings === 1 ? '' : 's'}. ` +
      'Completed trips and audit records keep the old name. Only edit to correct THIS place; ' +
      'to point somewhere else, create a new place instead.'
    );
  });

  @Input()
  show!: boolean;
  @Output()
  hide = new EventEmitter(true);
  @Output()
  refresh = new EventEmitter<void>();

  selectedGeolocation = signal<google.maps.places.PlaceResult | null>(null);
  formEnabled = signal<boolean>(false);
  searchPerformed = signal<boolean>(false);

  form = this.formBuilder.group({
    name: [{ value: '', disabled: true }, [Validators.required]],
    placeId: ['', [Validators.required]],
    lat: [{ value: 0, disabled: true }, [Validators.required, Validators.min(-90), Validators.max(90)]],
    lng: [{ value: 0, disabled: true }, [Validators.required, Validators.min(-180), Validators.max(180)]],
    placeType: [{ value: 'Terminal', disabled: true }, [Validators.required]],
    radius: [{ value: 1500, disabled: true }, [Validators.required, Validators.min(150)]],
    country: ['', [Validators.required]],
  });

  placeOptions = {
    componentRestrictions: { country: ['GH', 'UG'] },
    strictBounds: true,
  };
  terminalTypesEnum = PlaceTypeEnum;
  center: google.maps.LatLngLiteral = {
    lat: 0,
    lng: 0,
  };
  zoom = 19;
  markers: MapMarkerDto[] = [];
  circles: CircleMarkerDto[] = [];
  name = this.form.get('name');
  radius = this.form.get('radius');

  // Store the original place data for search initialization
  originalPlaceData: {
    placeId?: string;
    name?: string;
    address?: string;
  } = {};

  tempAddress!: google.maps.places.PlaceResult;

  // Removed ViewChild for deprecated autocomplete directive
  @ViewChild('placesInput') placesInput!: ElementRef;
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild(PlaceAutocompleteComponent, { static: false }) placeAutocomplete!: PlaceAutocompleteComponent;

  mutation = injectMutation(() => ({
    mutationFn: (payload: UpdateStandardPlaceDto) =>
      lastValueFrom(
        this.standardPlacesApi.standardPlaceControllerUpdateTerminal({
          id: this.place.id,
          body: payload,
        }),
      ),
    onSuccess: () => {
      this.clearErrors();
      this.hide.emit(true);
      this.refresh.emit();
      this.notification.success(`Standard place updated successfully`, 'Your changes have been saved to the database.');
      this.form.reset();
      this.formEnabled.set(false);
      this.searchPerformed.set(false);
      this.markers = [];
      this.circles = [];
    },
    onError: (error: any) => {
      this.logger.error('Error updating standard place', error);
      const result = this.formErrorHandler.handleFormError(error, { formName: 'admin-edit-standard-place-form' });
      this.errorDetails = result.problemDetail ?? null;
      this.showError.set(!!this.errorDetails);
    },
  }));

  constructor(private notification: NzNotificationService) {}

  // Standardized error handling
  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  private _place!: StandardPlaceDto;

  get place(): StandardPlaceDto {
    return this._place;
  }

  @Input()
  set place(value: StandardPlaceDto) {
    if (value) {
      this._place = value;

      // Refresh the blast-radius counts for the warning alert. Failure just
      // keeps the generic warning — never blocks editing.
      this.impact.set(null);
      this.impactService.getImpact(value.id).subscribe({
        next: (counts) => this.impact.set(counts),
        error: () => this.impact.set(null),
      });

      // Store original place data for search initialization
      this.originalPlaceData = {
        placeId: value.googleMapPlaceId,
        name: value.name,
        address: value.name, // Use name as fallback for address
      };

      // Populate form with current values
      this.form.patchValue({
        name: value.name,
        placeId: value.googleMapPlaceId,
        lat: value.lat,
        lng: value.lng,
        placeType: value.type,
        radius: value.radius,
        country: value.country || '',
      });

      this.center = {
        lat: value.lat,
        lng: value.lng,
      };

      this.tempAddress = {
        address_components: [],
        adr_address: '',
        formatted_address: '',
        formatted_phone_number: '',
        html_attributions: [],
        icon: '',
        international_phone_number: '',
        opening_hours: undefined,
        permanently_closed: false,
        photos: [],
        price_level: 0,
        rating: 0,
        reviews: [],
        types: [],
        url: '',
        utc_offset: 0,
        vicinity: '',
        website: '',
        place_id: value.googleMapPlaceId,
        name: value.name,
        geometry: {
          location: {
            lat(): number {
              return value.lat;
            },
            lng(): number {
              return value.lng;
            },
            equals(_other: LatLng): boolean {
              return true;
            },
            toJSON(): LatLngLiteral {
              return {
                lat: value.lat,
                lng: value.lng,
              };
            },
            toString(): string {
              return '';
            },
            toUrlValue(_precision?: number): string {
              return '';
            },
          },
          viewport: undefined,
        },
      };

      // Initialize map with current place data
      this.addCircle(this.tempAddress);
      this.addMarker(this.tempAddress);
    }
  }

  // Flag to prevent circular updates between form and map
  private updatingFromMap = false;

  // Preset radius values (accessible in template)
  readonly PRESETS = {
    busStop: 800,
    terminal: 1500,
    busStop2x: 1600,
    terminal2x: 3000,
    busStop3x: 2400,
    terminal3x: 4500,
  } as const;

  // Get default radius based on place type
  private getDefaultRadiusForPlaceType(placeType: PlaceTypeEnum | string | null): number {
    switch (placeType) {
      case PlaceTypeEnum.BusStop:
        return this.PRESETS.busStop;
      case PlaceTypeEnum.Terminal:
      case PlaceTypeEnum.TerminalBusStop:
        return this.PRESETS.terminal;
      default:
        return this.PRESETS.terminal;
    }
  }

  // Get dynamic step based on current radius value
  private getStepForRadius(radius: number): number {
    if (radius < 1000) {
      return 10; // 10m steps for radii < 1km
    } else if (radius < 10000) {
      return 50; // 50m steps for radii 1km-10km
    } else {
      return 100; // 100m steps for radii > 10km
    }
  }

  // Configuration for ngx-slider
  sliderOptions: Options = {
    floor: 150,
    ceil: 100000,
    step: 10,
    showTicks: false,
    showTicksValues: false,
    animate: true,
    showSelectionBar: true,
    getSelectionBarColor: () => '#2563eb', // Tailwind blue-600
    getPointerColor: () => '#2563eb',
    translate: (value: number): string => {
      return value + ' m';
    },
  };

  ngOnInit(): void {
    this.initializeSearchField();
    console.log('form group:', this.form);

    // Update default radius when placeType changes
    this.form.get('placeType')?.valueChanges.subscribe((placeType) => {
      if (placeType && this.formEnabled()) {
        const defaultRadius = this.getDefaultRadiusForPlaceType(placeType);
        // Always update to the default radius for the selected place type
        this.radius?.setValue(defaultRadius);
      }
    });

    // Listen for changes in the radius form control and update the circle
    this.radius?.valueChanges
      .pipe(
        debounceTime(50), // Add debounce to reduce multiple rapid updates
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        console.log('Radius form value changed:', value);

        // Update slider step based on current radius
        if (value) {
          this.sliderOptions = {
            ...this.sliderOptions,
            step: this.getStepForRadius(value),
          };
        }

        // Only update the circle if this change didn't come from the map
        if (!this.updatingFromMap && value && this.circles.length > 0) {
          console.log('Updating circle radius from form:', value);

          // Clone the circles array to trigger change detection
          const updatedCircles = [...this.circles];

          // Update the radius of the first circle while preserving all other properties
          updatedCircles[0] = {
            ...updatedCircles[0],
            radius: value,
          };

          // Replace the circles array with the updated one
          this.circles = updatedCircles;

          // Ensure the map view fits the updated circle size
          setTimeout(() => this.fitCircleInView(), 100);
        }

        // Reset the flag after handling the event
        this.updatingFromMap = false;
      });
  }

  /**
   * Apply a preset radius value
   */
  applyPreset(presetValue: number): void {
    if (this.radius && this.formEnabled()) {
      this.radius.setValue(presetValue);
    }
  }

  /**
   * Attempt to initialize the search field with the current place data
   */
  initializeSearchField(): void {
    // Check if we have place data to initialize with
    if (this._place) {
      console.log('Initializing search field with place:', this._place);

      setTimeout(() => {
        // Set only the form values that actually exist
        this.name?.setValue(this._place.name);
        const defaultRadius = this.getDefaultRadiusForPlaceType(this._place.type);
        this.radius?.setValue(this._place.radius || defaultRadius);

        // Get placeType from the form and use the correct property from StandardPlaceDto
        const placeType = this.form.get('placeType');
        if (placeType && this._place.type) {
          placeType.setValue(this._place.type);
        }

        // Enable form fields after setting data
        this.enableFormFields();

        // Create a mock place result to initialize the map with
        const mockPlaceResult = {
          geometry: {
            location: {
              // Use correct property names from StandardPlaceDto
              lat: () => this._place.lat,
              lng: () => this._place.lng,
            },
          },
          name: this._place.name,
        } as google.maps.places.PlaceResult;

        // Initialize the map markers and circle
        this.addMarker(mockPlaceResult);
        this.addCircle(mockPlaceResult);
      }, 100); // Short delay to ensure form controls are ready
    }
  }

  /**
   * Enable form fields after a place is searched
   */
  enableFormFields(): void {
    if (!this.formEnabled()) {
      this.form.get('name')?.enable();
      this.form.get('placeType')?.enable();
      this.form.get('radius')?.enable();
      this.form.get('lat')?.enable();
      this.form.get('lng')?.enable();
      this.formEnabled.set(true);
    }
  }

  closeDrawer() {
    this.hide.emit(true);
    this.show = false;
  }

  saveChanges() {
    // Clear any previous errors
    this.clearErrors();

    // Validate that a place has been searched for and found
    if (!this.searchPerformed()) {
      this.notification.warning('No search performed', 'Please search and select a place before saving changes.');
      return;
    }

    // Validate form values
    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAsTouched();
        control?.markAsDirty();
      });

      this.notification.warning('Invalid form', 'Please complete all required fields correctly.');
      return;
    }

    const payload: UpdateStandardPlaceDto = {
      id: this.place.id,
      type: this.form.get('placeType')!.value as PlaceTypeEnum,
      name: this.form.get('name')!.value!,
      googleMapPlaceId: this.form.get('placeId')!.value!,
      lng: this.form.get('lng')!.value!,
      lat: this.form.get('lat')!.value!,
      radius: this.form.get('radius')!.value!,
      country: this.form.get('country')!.value!,
    };

    this.logger.log('Update standard place payload', payload);

    // Submit the form using the mutation
    this.mutation.mutate(payload);
  }

  onInputChanged(value: string) {
    this.logger.log('🔍 Edit component onInputChanged called with value:', value);
    // Clear the form when user starts typing a new search
    if (value && value !== this.form.get('name')?.value) {
      this.logger.log('🔍 Clearing form data due to new search input in edit component');
      this.clearFormData();
    }
  }

  private clearFormData() {
    this.selectedGeolocation.set(null);
    this.form.get('name')?.setValue('');
    this.form.get('placeId')?.setValue('');
    this.form.get('lat')?.setValue(0);
    this.form.get('lng')?.setValue(0);
    this.form.get('country')?.setValue('');
    this.markers = [];
    this.circles = [];

    // Reset form validation state
    this.form.get('name')?.markAsUntouched();
    this.form.get('name')?.markAsPristine();
    this.form.get('placeId')?.markAsUntouched();
    this.form.get('placeId')?.markAsPristine();
    this.form.get('lat')?.markAsUntouched();
    this.form.get('lat')?.markAsPristine();
    this.form.get('lng')?.markAsUntouched();
    this.form.get('lng')?.markAsPristine();
    this.form.get('country')?.markAsUntouched();
    this.form.get('country')?.markAsPristine();

    // Update form validation status
    this.form.updateValueAndValidity();
  }

  public handleAddressChange(address: any) {
    this.logger.log('🔍 Edit component handleAddressChange called with:', address);
    this.logger.log('🔍 Address type:', typeof address);
    this.logger.log('🔍 Address keys:', address ? Object.keys(address) : 'null');

    // Handle null/empty address
    if (!address) {
      this.logger.log('❌ Address is null/empty, clearing form data');
      this.clearFormData();
      return;
    }

    this.selectedGeolocation.set(address);
    this.logger.log('Selected geolocation set in edit component:', address);

    const photoUrl =
      address.photos?.[0]?.getUrl({
        maxWidth: 500,
        maxHeight: 500,
      }) ?? null;

    const lat = address.geometry?.location?.lat();
    const lng = address.geometry?.location?.lng();

    const country = address.address_components?.find((component: any) =>
      component.types.includes('country'),
    )?.long_name;

    // Validate required location data
    if (lat === undefined || lng === undefined) {
      this.logger.error('Location data is undefined', { address, lat, lng });
      this.notification.error('Could not determine the location coordinates', '');
      return;
    }

    this.logger.log('Location data extracted:', { lat, lng, country });

    // set the form values
    this.logger.log('🔍 Setting form values:', {
      name: address.name,
      placeId: address.place_id,
      lat: lat,
      lng: lng,
      country: country,
      photoUrl: photoUrl,
    });

    this.logger.log('🔍 Address object structure:', {
      hasPlaceId: 'place_id' in address,
      placeIdValue: address.place_id,
      addressKeys: Object.keys(address),
    });

    this.form.get('name')?.setValue(address.name ?? null);

    // Handle placeId - use address.place_id or generate a fallback
    const placeIdValue = address.place_id || `manual_${Date.now()}_${lat}_${lng}`;
    this.logger.log('🔍 Setting placeId:', placeIdValue);
    this.form.get('placeId')?.setValue(placeIdValue);

    this.form.get('lat')?.setValue(lat);
    this.form.get('lng')?.setValue(lng);
    this.form.get('country')?.setValue(country ?? null);

    this.logger.log('🔍 Form values after setting:', {
      name: this.form.get('name')?.value,
      placeId: this.form.get('placeId')?.value,
      lat: this.form.get('lat')?.value,
      lng: this.form.get('lng')?.value,
      country: this.form.get('country')?.value,
      placeType: this.form.get('placeType')?.value,
      radius: this.form.get('radius')?.value,
    });

    // Mark form controls as touched and dirty to trigger validation
    this.form.get('name')?.markAsTouched();
    this.form.get('name')?.markAsDirty();
    this.form.get('placeId')?.markAsTouched();
    this.form.get('placeId')?.markAsDirty();
    this.form.get('lat')?.markAsTouched();
    this.form.get('lat')?.markAsDirty();
    this.form.get('lng')?.markAsTouched();
    this.form.get('lng')?.markAsDirty();
    this.form.get('country')?.markAsTouched();
    this.form.get('country')?.markAsDirty();

    // Update form validation status with a delay to ensure all controls are set
    setTimeout(() => {
      this.logger.log('🔍 Form values before validation:', {
        name: this.form.get('name')?.value,
        placeId: this.form.get('placeId')?.value,
        lat: this.form.get('lat')?.value,
        lng: this.form.get('lng')?.value,
        country: this.form.get('country')?.value,
        placeType: this.form.get('placeType')?.value,
        radius: this.form.get('radius')?.value,
      });

      this.form.updateValueAndValidity();

      // Debug form validation status
      this.logger.log('🔍 Form validation status after place selection:', {
        valid: this.form.valid,
        invalid: this.form.invalid,
        errors: this.form.errors,
        name: this.form.get('name')?.value,
        placeId: this.form.get('placeId')?.value,
        lat: this.form.get('lat')?.value,
        lng: this.form.get('lng')?.value,
        country: this.form.get('country')?.value,
        placeType: this.form.get('placeType')?.value,
        radius: this.form.get('radius')?.value,
        nameErrors: this.form.get('name')?.errors,
        placeIdErrors: this.form.get('placeId')?.errors,
        latErrors: this.form.get('lat')?.errors,
        lngErrors: this.form.get('lng')?.errors,
        countryErrors: this.form.get('country')?.errors,
        placeTypeErrors: this.form.get('placeType')?.errors,
        radiusErrors: this.form.get('radius')?.errors,
      });

      // Log validation status
      if (this.form.invalid) {
        this.logger.log('❌ Form still invalid after place selection');
      } else {
        this.logger.log('✅ Form is now valid!');
      }
    }, 200); // Increased delay to ensure all values are set

    // Update radius to smart default based on current placeType when place is selected
    // This ensures the radius matches the place type (800m for BusStop, 1500m for Terminal)
    const placeType = this.form.get('placeType')?.value || 'Terminal';
    const smartDefaultRadius = this.getDefaultRadiusForPlaceType(placeType);
    const currentRadius = this.radius?.value || 0;

    // If radius is still at initial default or hasn't been customized, update to smart default
    // Check if it's the initial default (1500) or a generic value like 2000
    if (currentRadius === 1500 || currentRadius === 0 || currentRadius === 2000) {
      this.radius?.setValue(smartDefaultRadius);
    }

    // Update center
    this.center = {
      lat: lat,
      lng: lng,
    };

    // Update map elements
    this.addMarker(address);
    this.addCircle(address);

    // Pan map to new location
    if (this.map?.googleMap) {
      this.map.googleMap.panTo(this.center);
    }

    // Enable form fields after search
    this.enableFormFields();
    this.searchPerformed.set(true);
  }

  addMarker(address: google.maps.places.PlaceResult) {
    this.markers = [];
    this.markers.push({
      id: v4(),
      position: {
        lat: address.geometry!.location!.lat()!,
        lng: address.geometry!.location!.lng()!,
      },
      label: {
        color: 'green',
        text: address.name || 'Location marker',
      },
      title: address.name || 'Location',
      info: address.formatted_address || address.name || '',
      options: {
        animation: google.maps.Animation.DROP, // Less distracting than BOUNCE
      },
    });
  }

  /**
   * Handle circle radius changes from the map
   */
  circleRadiusChanged(event: any): void {
    if (this.updatingFromMap) {
      return;
    }

    this.updatingFromMap = true;

    // Ensure we have a valid number
    const newRadius = typeof event === 'number' ? event : this.radius?.value || 50;

    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      // Update the form control
      this.form.get('radius')?.setValue(newRadius, { emitEvent: false });

      // Update the circle on the map
      if (this.circles.length > 0) {
        this.circles[0].radius = newRadius;
      }

      // Fit the map to the new circle size
      setTimeout(() => {
        this.fitCircleInView();
        // Reset the flag after updates are complete
        setTimeout(() => {
          this.updatingFromMap = false;
        }, 100);
      }, 0);
    }, 0);
  }

  /**
   * Alternative method to handle circle changes (more reliable than radius events)
   * This gets triggered when the circle is moved or resized
   */
  circleCenterChanged(event: any): void {
    try {
      if (event && event.target) {
        const circle = event.target as google.maps.Circle;
        if (circle && typeof circle.getRadius === 'function') {
          const newRadius = Math.round(circle.getRadius());
          // Only update if significantly different to avoid loops
          const currentRadius = this.radius?.value || 0;
          if (Math.abs(newRadius - currentRadius) > 5) {
            console.log('Updating radius from center change:', newRadius);

            // Set the flag to prevent circular updates
            this.updatingFromMap = true;

            // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
              this.radius?.setValue(newRadius, { emitEvent: false });

              // Update the circle on the map
              if (this.circles.length > 0) {
                this.circles[0].radius = newRadius;
              }

              // Fit the map to the new circle size
              setTimeout(() => {
                this.fitCircleInView();
                // Reset the flag after updates are complete
                setTimeout(() => {
                  this.updatingFromMap = false;
                }, 100);
              }, 0);
            }, 0);
          }
        }
      }
    } catch (error) {
      console.error('Error handling circle center change:', error);
    }
  }

  addCircle(address: google.maps.places.PlaceResult) {
    this.circles = [];

    // Create the circle with all required properties for CircleMarkerDto
    this.circles.push({
      id: v4(),
      center: {
        lat: address.geometry!.location!.lat()!,
        lng: address.geometry!.location!.lng()!,
      },
      radius: this.radius?.value || 500,
      name: address.name || 'Location',
      type: 'standardPlace', // Required by CircleMarkerDto
      routeId: null, // Required by CircleMarkerDto
      fillColor: '#4285F4',
      fillOpacity: 0.2,
      strokeColor: '#4285F4',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      editable: false, // Set to false to avoid passive event issues
      draggable: false,
      clickable: true,
      zIndex: 1,
    });

    // Set initial center (will be adjusted by fitCircleInView)
    this.center = {
      lat: address.geometry!.location!.lat()!,
      lng: address.geometry!.location!.lng()!,
    };

    // Wait for the circle to be rendered, then fit it to the view
    setTimeout(() => this.fitCircleInView(), 200);
  }

  /**
   * Adjust the circle radius by the specified amount
   * This provides a safe way to adjust the circle size without triggering passive event listener warnings
   */
  adjustRadius(change: number): void {
    if (!this.radius) return;

    const currentValue = this.radius.value || 150;
    const newValue = Math.max(150, currentValue + change); // Ensure minimum of 150m radius

    console.log(`Adjusting radius from ${currentValue} to ${newValue}`);

    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.radius?.setValue(newValue, { emitEvent: false });

      // Update the circle directly (redundant with valueChanges subscription, but makes UI more responsive)
      if (this.circles.length > 0) {
        const updatedCircles = [...this.circles];
        updatedCircles[0] = {
          ...updatedCircles[0],
          radius: newValue,
        };
        this.circles = updatedCircles;

        // Auto-fit map to the updated circle
        setTimeout(() => {
          this.fitCircleInView();
        }, 0);
      }
    }, 0);
  }

  /**
   * Fit the current circle in the map view
   * This ensures the entire circle is visible as radius changes
   */
  fitCircleInView(): void {
    if (!this.map || this.circles.length === 0) return;

    // Create bounds from the circle
    const bounds = this.mapService.getMapBoundaries(this.markers, this.circles);

    // Apply the bounds with padding
    this.map.fitBounds(bounds, 50);

    // Log for debugging
    console.log(`Adjusted map view to fit circle with radius: ${this.circles[0].radius}m`);
  }

  onMapInit(map: google.maps.Map) {
    // Enable native map controls
    map.setOptions({
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      fullscreenControl: true,
    });

    // Fit map to markers and circles
    map.fitBounds(this.mapService.getMapBoundaries(this.markers, this.circles), 50);
  }
}
